import { videoKind } from "./coachExercise";

/* ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * W137 R3b point 3 — WHAT THE EXERCISE LOOK WINDOW SHOWS (Fable's ruling on his "Only if we click the exercise text it can
 * open to see it"): the library row's video, THIS day's prescription, the library row's steps and notes — read-only. The
 * decisions, pure, so `exerciseLook.test.ts` executes them; `components/ExerciseLookWindow.tsx` draws them and writes nothing.
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

/** The library row as the window reads it (ONE read on open). */
export type LibraryLook = { demo_video_url: string | null; instructions: string[] | null; notes: string | null };

export type LookVideo =
  | { kind: "file"; src: string }
  /** A page (YouTube, Vimeo, …): a `<video>` cannot play it — the library editor's own answer is a link out, reused. */
  | { kind: "link"; href: string; host: string }
  | { kind: "none" };

export type LookContent =
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "ready"; video: LookVideo; steps: string[]; notes: string | null };

/** The head: `A1 · Weighted Pull-Up`, or the name alone when the row has no label. */
export function lookTitle(label: string | null, name: string): string {
  return label === null ? name : `${label} · ${name}`;
}

function host(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; }
}

/** The library row → what the body draws. `undefined` = still reading; `null` = the read failed or found nothing. */
export function lookContent(row: LibraryLook | null | undefined, origin?: string): LookContent {
  if (row === undefined) return { kind: "loading" };
  if (row === null) return { kind: "failed" };
  const url = (row.demo_video_url ?? "").trim();
  const video: LookVideo = url === "" ? { kind: "none" }
    : videoKind(url, origin) === "file" ? { kind: "file", src: url } : { kind: "link", href: url, host: host(url) };
  const steps = (row.instructions ?? []).map((s) => s.trim()).filter((s) => s !== "");
  const notes = (row.notes ?? "").trim();
  return { kind: "ready", video, steps, notes: notes === "" ? null : notes };
}

/** The words the window uses, in one place: the phone preview's `No video yet`, the editor's `How to do it`. */
export const LOOK_WORDS = {
  noVideo: "No video yet",
  howTo: "How to do it",
  failed: "Could not load the exercise",
  openLink: "Open the linked video",
  editSets: "Edit sets",
} as const;
